import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { TranslateService } from '@ngx-translate/core';
import { CartService } from '../services/cart.service';
import { DataService } from '../services/data.service';
import { CartLineView, PaymentMethod } from '../models/cart.model';
import { cartSubtotal, toCartLineView } from './cart.utils';
import { findSuperSectionFor } from '../menu/menu.utils';
import { CommentSheetComponent } from '../shared/comment-sheet/comment-sheet.component';
import { VoucherMode, PaymentMode, VenueType } from '../shared/enums';
import { MenuService } from '../menu/menu.service';
import { CartCheckoutService } from './cart-checkout.service';
import { AlertDialogComponent } from '../shared/alert-dialog/alert-dialog.component';
import { ModifierComponent } from '../shared/modifier/modifier.component';
import { currencySymbol } from '../shared/currency.utils';

@Component({
  selector: 'app-cart',
  templateUrl: './cart.component.html',
  styleUrl: './cart.component.scss',
  standalone: false,
})
export class CartComponent implements OnInit {
  private readonly cartService = inject(CartService);
  private readonly dataService = inject(DataService);
  private readonly translateService = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly bottomSheet = inject(MatBottomSheet);
  private readonly menuService = inject(MenuService);

  readonly checkout = inject(CartCheckoutService);

  readonly VoucherMode = VoucherMode;

  readonly orderNote = signal('');

  // Labels a line with its super-section name only when the same item name shows up in
  // more than one line - the same item added from two different sections stays deliberately
  // separate (see project memory on combo scoping), so this is purely about telling them
  // apart on an otherwise-identical-looking cart row, not about merging them.
  readonly lines = computed<CartLineView[]>(() => {
    const rawLines = this.cartService.cartLines().map(toCartLineView);

    const nameCounts = new Map<string, number>();
    rawLines.forEach(line => nameCounts.set(line.itemName, (nameCounts.get(line.itemName) ?? 0) + 1));

    const sections = this.menuService.menu()?.sections ?? [];
    return rawLines.map(line => {
      if ((nameCounts.get(line.itemName) ?? 0) <= 1 || !line.subSectionId) return line;

      const superSection = findSuperSectionFor(sections, line.subSectionId);
      return superSection ? { ...line, sectionLabel: superSection.name } : line;
    });
  });

  readonly subtotal = computed(() => cartSubtotal(this.lines()));

  readonly isReadOnly = computed(() => this.dataService.restaurant().type == VenueType.ReadOnly);
  readonly isPrepaid = computed(() => this.dataService.restaurant().payment_mode == PaymentMode.Prepaid);
  readonly isPayNowOnly = computed(() =>
    this.dataService.restaurant().payment_mode == PaymentMode.PayAtTable && !!this.dataService.zone()?.pay_now_only);

  // Plain methods, not computed(): isPayNowEnabled is a mutable DataService field,
  // not a signal, so a computed() here would memoize a stale value across changes.
  isPayAtTable(): boolean {
    return this.dataService.restaurant().payment_mode == PaymentMode.PayAtTable;
  }

  isPayNowEnabled(): boolean {
    return this.dataService.isPayNowEnabled;
  }

  // Pay at Table mode always adds to the bill; "Pay Now" is only offered on top of
  // that when the venue has it enabled — the two are independent settings.
  canPayNow(): boolean {
    return this.isPayAtTable() && this.isPayNowEnabled();
  }

  readonly currency = computed(() => currencySymbol(this.dataService.restaurant().currency));
  readonly orderMessage = computed(() => this.menuService.menu()?.order_message);

  readonly showMinOrderMessage = computed(() =>
    this.checkout.minOrder() > 0 &&
    this.subtotal() < this.checkout.minOrder() &&
    this.isPrepaid());

  readonly hasOrderInfo = computed(() =>
    !this.isReadOnly() || !!this.orderMessage() || this.showMinOrderMessage());

  constructor() {
    // Prepaid checkout (voucher discount, fees, final total) is server-computed and
    // must be refreshed whenever the cart contents or applied voucher change. Kept
    // here (not in the service) so the effect is torn down with this component.
    effect(() => {
      if (this.isPrepaid()) {
        this.checkout.applyCheckout(this.checkout.voucher());
      }
    });
  }

  ngOnInit(): void {
    this.checkout.init();
  }

  clearCart(): void {
    AlertDialogComponent.open(this.dialog, {
      type: 'warning',
      title: 'CART.CLEAR',
      message: 'CART.CONFIRM_CLEAR',
      enableCancelBtn: true,
    }).afterClosed().subscribe(confirmed => {
      if (confirmed) this.cartService.clearCart();
    });
  }

  increaseQty(line: CartLineView): void {
    this.cartService.incrementLine(line.id);
  }

  decreaseQty(line: CartLineView): void {
    this.cartService.decrementLine(line.id);
  }

  addComment(): void {
    this.bottomSheet.open(CommentSheetComponent, {
      data: { title: this.translateService.instant('ORDER.NOTES'), comment: this.orderNote() },
      autoFocus: false,
    }).afterDismissed().subscribe(result => {
      if (result === undefined) return;
      this.orderNote.set(result);
    });
  }

  editLine(line: CartLineView): void {
    const cartLine = this.cartService.cartLines().find(candidate => candidate.id === line.id);
    if (!cartLine) return;

    this.bottomSheet.open(ModifierComponent, {
      autoFocus: false,
      data: {
        item: cartLine.item,
        subSectionId: cartLine.subSectionId,
        isOptionalSubSection: cartLine.isOptionalSubSection,
        cartLine,
      },
    });
  }

  submit(): Promise<void> {
    return this.checkout.submit(this.orderNote());
  }

  onPrepaidPaymentSelected(method: PaymentMethod): Promise<void> {
    return this.checkout.onPrepaidPaymentSelected(method, this.orderNote());
  }

  displayCheckout(): void {
    if (!this.canPayNow()) return;

    this.checkout.displayCheckout(this.orderNote());
  }
}
